import { nanoid } from 'nanoid';
import Swiper from 'swiper/bundle';

/**
 * Initialize a Swiper instance for a given element.
 * @param el
 * @param swiperClass
 * @param options
 */
const initializeSwiper = (el, swiperClass, options) => {
    if (!(el instanceof Element)) {
        console.error('Error: The provided element is not a valid DOM element.');
        return;
    }

    if (el.classList.contains('swiper-initialized') || el.dataset.swiperInitialized) return; // Prevent re-initialization
    el.dataset.swiperInitialized = 'true';

    const swiper = new Swiper(swiperClass, options);

    // Pause autoplay on hover, resume on mouse out
    el.addEventListener('mouseover', () => swiper.autoplay?.stop());
    el.addEventListener('mouseout', () => options.autoplay && swiper.autoplay?.start());

    return swiper;
};

/**
 * Generate unique class names for Swiper instance.
 * @returns {Object} - Object containing unique class names.
 */
const generateClasses = () => {
    const rand = nanoid(10);
    return {
        rand: rand,
        swiperClass: `swiper-${rand}`,
        nextClass: `next-${rand}`,
        prevClass: `prev-${rand}`,
        paginationClass: `pagination-${rand}`,
        scrollbarClass: `scrollbar-${rand}`,
    };
};

/**
 * Default Swiper options.
 * @returns {Object} - Default Swiper configuration.
 */
const getDefaultOptions = () => ({
    grabCursor: true,
    allowTouchMove: true,
    threshold: 5,
    autoHeight: false,
    loop: false,
    hashNavigation: false,
    direction: 'horizontal',
    freeMode: false,
    cssMode: false,
    centeredSlides: false,
    slidesPerView: 'auto',
});

/**
 * Parse options safely
 * @param el
 * @returns {{}|any|{}}
 */
const parseOptions = (el) => {
    try {
        return JSON.parse(el.dataset.options) || {};
    } catch (e) {
        console.error('Invalid JSON in data-options', e);
        return {};
    }
};

// Initialize Swipers
const initializeSwipers = () => {
    const swiperElements = document.querySelectorAll('.w-swiper');
    swiperElements.forEach((el) => {
        if (el.classList.contains('swiper-initialized')) return; // Prevent re-initialization

        const classes = generateClasses();
        el.classList.add(classes.swiperClass);

        const container = el.closest('.swiper-container');

        // Create or get control container
        let controls = container?.querySelector('.swiper-controls');
        if (!controls) {
            controls = document.createElement('div');
            controls.classList.add('swiper-controls');
            el.after(controls);
        }

        let options = parseOptions(el);
        let swiperOptions = { ...getDefaultOptions() };

        // Parse specific options
        [
            'autoHeight',
            'loop',
            'freeMode',
            'cssMode',
            'mousewheel',
            'parallax',
            'hashNavigation',
        ].forEach(key => options[key] && (swiperOptions[key] = true));

        swiperOptions.wrapperClass = String(options.wrapperClass || 'swiper-wrapper');
        swiperOptions.slideClass = String(options.slideClass || 'swiper-slide');
        swiperOptions.slideActiveClass = String(options.slideActiveClass || 'swiper-slide-active');

        swiperOptions.direction = String(options.direction || 'horizontal');
        swiperOptions.slidesPerView = options.slidesPerView || 'auto';
        swiperOptions.spaceBetween = parseInt(options.spaceBetween, 10) || 0;
        swiperOptions.speed = parseInt(options.speed, 10) || 300;

        // Grid settings
        if (options.grid) {
            swiperOptions.grid = {
                rows: Math.max(parseInt(options.grid?.rows) || 1, 1),
                fill: options.grid?.fill || 'row',
            };
            if (options.loop) swiperOptions.loopAddBlankSlides = true;
        }

        // Autoplay settings
        if (options.autoplay) {
            swiperOptions.autoplay = {
                delay: parseInt(options.autoplay?.delay) || 3000,
                disableOnInteraction: options.autoplay?.disableOnInteraction || true,
                reverseDirection: options.autoplay?.reverseDirection || false,
            };
        }

        // Navigation controls
        if (options.navigation) {
            let btnPrev = container?.querySelector('.swiper-button-prev');
            let btnNext = container?.querySelector('.swiper-button-next');

            if (!btnPrev) {
                btnPrev = document.createElement('div');
                btnPrev.classList.add('swiper-button', 'swiper-button-prev');
                btnPrev.setAttribute('data-fa', '');
                controls.append(btnPrev);
            }
            if (!btnNext) {
                btnNext = document.createElement('div');
                btnNext.classList.add('swiper-button', 'swiper-button-next');
                btnNext.setAttribute('data-fa', '');
                controls.append(btnNext);
            }

            btnPrev.classList.add(classes.prevClass);
            btnNext.classList.add(classes.nextClass);

            swiperOptions.navigation = {
                nextEl: `.${classes.nextClass}`,
                prevEl: `.${classes.prevClass}`,
            };
        }

        // Pagination controls
        if (options.pagination) {
            let pagination = container?.querySelector('.swiper-pagination');
            if (!pagination) {
                pagination = document.createElement('div');
                pagination.classList.add('swiper-pagination');
                controls.appendChild(pagination);
            }

            pagination.classList.add(classes.paginationClass);

            const paginationType = String(options.pagination);
            swiperOptions.pagination = {
                el: `.${classes.paginationClass}`,
                clickable: true,
                ...(paginationType === 'bullets' && { dynamicBullets: !0, type: 'bullets' }),
                ...(paginationType === 'fraction' && { type: 'fraction' }),
                ...(paginationType === 'progressbar' && { type: 'progressbar' }),
                ...(paginationType === 'custom' && {
                    renderBullet: (index, className) => `<span class="${className}">${index + 1}</span>`,
                }),
            };
        }

        // Scrollbar controls
        if (options.scrollbar) {
            let scrollbar = container?.querySelector('.swiper-scrollbar');
            if (!scrollbar) {
                scrollbar = document.createElement('div');
                scrollbar.classList.add('swiper-scrollbar');
                controls.appendChild(scrollbar);
            }

            scrollbar.classList.add(classes.scrollbarClass);

            swiperOptions.scrollbar = {
                el: `.${classes.scrollbarClass}`,
                hide: true,
                draggable: true,
            };
        }

        // observer
        if (options._observer) {
            swiperOptions.observer = !0;
            swiperOptions.observeParents = !0;
            swiperOptions.observeSlideChildren = !0;
        }

        // centeredSlides
        if (options._centered) {
            swiperOptions.centeredSlides = !0;
            swiperOptions.centeredSlidesBounds = !0;
        }

        // marquee
        if (options._marquee) {
            swiperOptions.centeredSlides = !1;
            swiperOptions.autoplay = {
                delay: 1,
                disableOnInteraction: !0,
            };
            swiperOptions.loop = !0;
            swiperOptions.speed = 6000;
            swiperOptions.allowTouchMove = !0;
        }

        // spaceBetween breakpoints
        if (options._gap) {
            swiperOptions.spaceBetween = 20;
            swiperOptions.breakpoints = {
                768: { spaceBetween: 28 },
            };
        }

        // breakpoints
        if (options._breakpoints) {
            swiperOptions.breakpoints = {
                0: options?._mobile || {},
                768: options?._tablet || {},
                1024: options?._desktop || {},
            };
        }

        initializeSwiper(el, `.${classes.swiperClass}`, swiperOptions);
    });
};

// 
const spgSwipers = () => {
    const swiperElements = [...document.querySelectorAll('.swiper-product-gallery')];

    swiperElements.forEach((el) => {
        const classes = generateClasses();
        el.classList.add(classes.swiperClass);

        const w_images = el?.querySelector('.swiper-images');
        const w_thumbs = el?.querySelector('.swiper-thumbs');

        let swiper_images = false;
        let swiper_thumbs = false;

        /** Thumbs */
        if (w_thumbs) {
            w_thumbs?.querySelector('.swiper-button-prev')?.classList.add('prev-thumbs-' + classes.rand);
            w_thumbs?.querySelector('.swiper-button-next')?.classList.add('next-thumbs-' + classes.rand);
            w_thumbs.classList.add('thumbs-' + classes.rand);

            const thumbs_options = {
                ...getDefaultOptions(),
                breakpoints: {
                    0: { spaceBetween: 5, slidesPerView: 4 },
                    768: { spaceBetween: 10, slidesPerView: 4 },
                    1024: { spaceBetween: 10, slidesPerView: 5 },
                },
                navigation: {
                    prevEl: '.prev-thumbs-' + classes.rand,
                    nextEl: '.next-thumbs-' + classes.rand,
                },
            };

            swiper_thumbs = initializeSwiper(w_thumbs, '.thumbs-' + classes.rand, thumbs_options);
        }

        /** Main images */
        if (w_images) {
            w_images?.querySelector('.swiper-button-prev')?.classList.add('prev-images-' + classes.rand);
            w_images?.querySelector('.swiper-button-next')?.classList.add('next-images-' + classes.rand);
            w_images.classList.add('images-' + classes.rand);

            const images_options = {
                ...getDefaultOptions(),
                slidesPerView: 'auto',
                spaceBetween: 10,
                watchSlidesProgress: true,
                navigation: {
                    prevEl: '.prev-images-' + classes.rand,
                    nextEl: '.next-images-' + classes.rand,
                },
                ...(swiper_thumbs && { thumbs: { swiper: swiper_thumbs } }),
            };

            swiper_images = initializeSwiper(w_images, '.images-' + classes.rand, images_options);
        }

        /** Lưu thông tin ảnh gốc */
        const firstImage = w_images?.querySelector('.swiper-images-first img');
        const firstImageSrc = firstImage?.getAttribute('src') || '';
        const firstImageAlt = firstImage?.getAttribute('alt') || '';
        const imagePopup = w_images?.querySelector('.swiper-images-first .image-popup');
        const firstThumb = w_thumbs?.querySelector('.swiper-thumbs-first img');
        const firstThumbSrc = firstThumb?.getAttribute('src') || '';
        const dataLargeImage = firstThumb?.getAttribute('data-large_image') || '';

        /** Sửa lỗi data-src = false khi chưa chọn variation */
        if (imagePopup && firstImageSrc) {
            imagePopup.setAttribute('data-src', firstImageSrc);
        }

        let lastAddedSlideIndex = null;

        /** WooCommerce variation event */
        jQuery(function ($) {
            $(document).on('found_variation', 'form.variations_form', function (event, variation) {
                const imageSrc = variation?.image?.src || firstImageSrc;
                const imageAlt = variation?.image?.alt || firstImageAlt;

                if (!imageSrc) return;

                if (lastAddedSlideIndex !== null) {
                    swiper_images.removeSlide(lastAddedSlideIndex);
                }

                const newSlide = document.createElement('div');
                newSlide.classList.add('swiper-slide');

                const newImg = document.createElement('img');
                newImg.setAttribute('src', imageSrc);
                newImg.setAttribute('data-src', imageSrc);
                newImg.setAttribute('data-large_image', imageSrc);
                newImg.setAttribute('alt', imageAlt);
                newImg.classList.add('wp-post-image');

                const newLink = document.createElement('a');
                newLink.classList.add('res', 'ar[1-1]', 'fcy-popup');
                newLink.setAttribute('href', imageSrc);
                newLink.appendChild(newImg);

                const newDiv = document.createElement('div');
                newDiv.setAttribute('data-thumb', imageSrc);
                newDiv.classList.add('wpg__image', 'cover');
                newDiv.appendChild(newLink);

                const newSpan = document.createElement('span');
                newSpan.setAttribute('data-rel', 'lightbox');
                newSpan.classList.add('image-popup');
                newSpan.setAttribute('data-src', imageSrc);
                newSpan.setAttribute('data-fa', '');
                newDiv.appendChild(newSpan);

                newSlide.appendChild(newDiv);
                swiper_images.appendSlide(newSlide);
                lastAddedSlideIndex = swiper_images.slides.length - 1;
                swiper_images.slideTo(lastAddedSlideIndex);
            });

            $(document).on('reset_image', 'form.variations_form', function () {
                if (firstImage && firstImageSrc) {
                    firstImage.setAttribute('src', firstImageSrc);
                }
                if (imagePopup && dataLargeImage) {
                    imagePopup.setAttribute('data-src', dataLargeImage);
                }
                if (swiper_thumbs && firstThumb && firstThumbSrc) {
                    firstThumb.setAttribute('src', firstThumbSrc);
                }
                swiper_images.slideTo(0);
            });
        });
    });
};
document.addEventListener('DOMContentLoaded', spgSwipers);
document.addEventListener('DOMContentLoaded', initializeSwipers);